import "../../utils/tailwind.css";
import { PageProvider, useAddCaseStudyProvider } from "./provider";
import { useState } from "react";
import Label from "../../components/label";
import Section from "../../components/section";
import Input, { FormikInput } from "../../components/input";
import Button from "../../components/Buttons";
import { Formik, Form, FieldArray, ErrorMessage } from "formik";
import ReactQuill from "react-quill";
import Switch from "../../components/switch";
import ImageUploader from "../../components/upload-image";
import Plus from "../../components/svg/plus";
import Trash from "../../components/svg/trash";
import slugify from "slugify";
import * as Yup from "yup";
import ScrolltoFormError from "../../components/scroll-to-form-error";
import { useNavigate } from "react-router-dom";

export default function AddCaseStudyPage() {
  return (
    <div className="p-3">
      <PageProvider>
        <PageContent />
      </PageProvider>
    </div>
  );
}

function PageContent() {
  const { initialValues, handleSubmit, isEditing } = useAddCaseStudyProvider();
  const [isSlugEdited, setIsSlugEdited] = useState(false);
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    caseStudy: Yup.object({
      title: Yup.string().required("Heading is required.").max(150, "Title must be at most 150 characters"),
      overview: Yup.string().required("Overview is required."),
      challenge: Yup.string().required("Challenge section is required."),
      solution: Yup.string().required("Solution section is required."),
      banner_img: Yup.string().required("Banner image is required.")
    }),
  });

  return (
    <Section className="h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <Label label={isEditing ? "Edit Case Study Details" : "Add Case Study Details"} className="text-[18px] text-black" />
        <button
          type="button"
          className="text-sm px-4 py-2 border rounded-md hover:bg-gray-100"
          onClick={() => navigate("/case-studies")}>
          Back to List
        </button>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={handleSubmit}>
        {({ values, setFieldValue, errors }) => (
          <Form>
            <ScrolltoFormError>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div className="flex items-center space-x-2">
                  <Label label="Is Published ?" className="text-black mr-4 mt-3 text-[15px]" />
                  <Switch
                    className="pt-1"
                    type="switch"
                    name="is_publish"
                    onChange={(val) => setFieldValue("caseStudy.is_publish", val)}
                    checked={values.caseStudy.is_publish}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col">
                  <Label label="Meta Title" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="caseStudy.meta_title" rootClassName="w-full" />
                </div>

                <div className="flex flex-col">
                  <Label label="Meta Keywords" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="caseStudy.meta_keywords" rootClassName="w-full" className="h-[26rem]" />
                </div>
                <div className="flex flex-col ">
                  <Label label="Meta Description" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="caseStudy.meta_description" rootClassName="w-full" className="h-[26rem]" />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 mt-2 gap-3">
                <div className="flex flex-col">
                  <Label label="Heading" className="mb-2 text-black text-[15px]" />
                  <FormikInput
                    name="caseStudy.title"
                    rootClassName="w-full"
                    value={values.caseStudy?.title || ""}
                    onChange={(e) => {
                      const newTitle = e.target.value;
                      setFieldValue("caseStudy.title", newTitle);
                      if (!isSlugEdited) {
                        setFieldValue("caseStudy.slug_url", slugify(newTitle, { lower: true }));
                      }
                    }}
                  />
                </div>

                <div className="flex flex-col">
                  <Label label="Slug URL" className="mb-2 text-black text-[15px]" />
                  <FormikInput
                    name="caseStudy.slug_url"
                    rootClassName="w-full"
                    value={values.caseStudy?.slug_url || ""}
                    onChange={(e) => {
                      setIsSlugEdited(true);
                      setFieldValue("caseStudy.slug_url", e.target.value);
                    }}
                  />
                </div>

                <div className="flex flex-col">
                  <Label label="Client Name" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="caseStudy.client" rootClassName="w-full" />
                </div>
                
                <div className="flex flex-col">
                  <Label label="Category" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="caseStudy.category" rootClassName="w-full" />
                </div>

                <div className="flex flex-col">
                  <Label label="Author" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="caseStudy.author" rootClassName="w-full" />
                </div>

                <div className="flex flex-col">
                  <Label label="Keywords (' , ' seperated)" className="mb-2 text-black text-[15px]" />
                  <FormikInput name="caseStudy.keywords" rootClassName="w-full" />
                </div>
              </div>

              <div className="w-full mt-3">
                <div className="flex flex-col mb-3">
                  <Label label="Overview" className="mb-2 text-black text-[15px]" />
                  <ReactQuill theme="snow" value={values.caseStudy.overview} onChange={(e) => setFieldValue("caseStudy.overview", e)} />
                  <ErrorMessage name="caseStudy.overview">
                    {(msg) => <div className="form-error text-red-500 mt-1 text-xs form-field-error">{msg}</div>}
                  </ErrorMessage>
                </div>

                <div className="flex flex-col mb-3">
                  <Label label="The Challenge" className="mb-2 text-black text-[15px]" />
                  <ReactQuill theme="snow" value={values.caseStudy.challenge} onChange={(e) => setFieldValue("caseStudy.challenge", e)} />
                  <ErrorMessage name="caseStudy.challenge">
                    {(msg) => <div className="form-error text-red-500 mt-1 text-xs form-field-error">{msg}</div>}
                  </ErrorMessage>
                </div>

                <div className="flex flex-col">
                  <Label label="Our Solution" className="mb-2 text-black text-[15px]" />
                  <ReactQuill theme="snow" value={values.caseStudy.solution} onChange={(e) => setFieldValue("caseStudy.solution", e)} />
                  <ErrorMessage name="caseStudy.solution">
                    {(msg) => <div className="form-error text-red-500 mt-1 text-xs form-field-error">{msg}</div>}
                  </ErrorMessage>
                </div>
              </div>

              <div className="w-full mt-5">
                <Label label="Key Highlight Metrics" className="mb-2 text-black text-[15px]" />
                <FieldArray name="caseStudy.metrics">
                  {({ push, remove }) => (
                    <div className="space-y-3">
                      {values.caseStudy.metrics?.map((metric, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="flex-1">
                            <Input
                              placeholder="Metric Value (e.g. +45% or 14)"
                              value={metric.value}
                              onChange={(e) => setFieldValue(`caseStudy.metrics.${idx}.value`, e.target.value)}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder="Metric Label (e.g. Distributor Inquiries)"
                              value={metric.label}
                              onChange={(e) => setFieldValue(`caseStudy.metrics.${idx}.label`, e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            onClick={() => remove(idx)}>
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="mt-2 text-sm bg-black text-white px-3 py-1.5 rounded flex items-center gap-2"
                        onClick={() => push({ label: "", value: "" })}>
                        <Plus className="w-3 h-3 text-white" />
                        <span>Add Key Metric</span>
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>

              <div className="w-full mt-5">
                <Label label="The Results (Bullet Points)" className="mb-2 text-black text-[15px]" />
                <FieldArray name="caseStudy.results">
                  {({ push, remove }) => (
                    <div className="space-y-3">
                      {values.caseStudy.results?.map((res, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="text-gray-400 font-bold text-sm">#{idx + 1}</span>
                          <div className="flex-1">
                            <Input
                              placeholder="e.g. 45% increase in international distributor inquiries within 3 months."
                              value={res}
                              onChange={(e) => setFieldValue(`caseStudy.results.${idx}`, e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            onClick={() => remove(idx)}>
                            <Trash className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="mt-2 text-sm bg-black text-white px-3 py-1.5 rounded flex items-center gap-2"
                        onClick={() => push("")}>
                        <Plus className="w-3 h-3 text-white" />
                        <span>Add Result Point</span>
                      </button>
                    </div>
                  )}
                </FieldArray>
              </div>

              <Label label="Banner Image" className="mt-5 mb-2 text-black text-[15px]" />
              <div className="w-full mb-16">
                <ImageUploader
                  imageUrl={values.caseStudy.banner_img}
                  onChange={(res) => {
                    setFieldValue(`caseStudy.banner_img`, res?.data?.file_url);
                  }}
                />
                <ErrorMessage name="caseStudy.banner_img">
                  {(msg) => <div className="form-error text-red-500 mt-1 text-xs form-field-error">{msg}</div>}
                </ErrorMessage>
              </div>

              <div className="fixed bottom-0 left-0 w-full bg-white flex justify-end items-center py-3 pr-4 space-x-3 shadow-md">
                <Button type="submit">Save</Button>
              </div>
            </ScrolltoFormError>
          </Form>
        )}
      </Formik>
    </Section>
  );
}
